import { PiChatCircle, PiTestTubeFill } from 'react-icons/pi';
import type {
  ComponentAppearance,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import { classes } from 'src/modules/shared/utils/styles.util';

interface TestConversationIconProps {
  appearance?: ComponentAppearance;
  className?: string;
  inlineContent?: boolean;
  size?: ComponentSize;
  variant?: ComponentVariant;
}

const iconSizeClasses: Record<ComponentSize, string> = {
  '2xs': 'h-3.5 w-3.5',
  xs: 'h-3.5 w-3.5',
  sm: 'h-[1.12rem] w-[1.12rem]',
  md: 'h-5 w-5',
  lg: 'h-5 w-5',
};

const inlineIconSizeClasses: Record<ComponentSize, string> = {
  '2xs': 'h-3.5 w-3.5',
  xs: 'h-4 w-4',
  sm: 'h-[1.12rem] w-[1.12rem]',
  md: 'h-5 w-5',
  lg: 'h-5 w-5',
};

const accentContainerClassesBySize: Record<ComponentSize, string> = {
  '2xs': '-bottom-[0.04rem] -right-[0.12rem] px-[0.02rem] py-[0.06rem]',
  xs: '-bottom-[0.05rem] -right-[0.14rem] px-[0.02rem] py-[0.07rem]',
  sm: '-bottom-[0.05rem] -right-[0.14rem] px-[0.02rem] py-[0.07rem]',
  md: '-bottom-[0.05rem] -right-[0.14rem] px-[0.02rem] py-[0.07rem]',
  lg: '-bottom-[0.05rem] -right-[0.14rem] px-[0.02rem] py-[0.07rem]',
};

const accentClassesBySize: Record<ComponentSize, string> = {
  '2xs': 'h-[0.5rem] w-[0.5rem] -rotate-[45deg]',
  xs: 'h-[0.56rem] w-[0.56rem] -rotate-[45deg]',
  sm: 'h-[0.62rem] w-[0.62rem] -rotate-[45deg]',
  md: 'h-[0.62rem] w-[0.62rem] -rotate-[45deg]',
  lg: 'h-[0.62rem] w-[0.62rem] -rotate-[45deg]',
};

const accentBackgroundClassMap: Record<ComponentVariant, string> = {
  primary: 'bg-primary-50',
  secondary: 'bg-secondary-50',
  neutral: 'bg-neutral-50',
  danger: 'bg-danger-50',
  warning: 'bg-warning-50',
  success: 'bg-success-50',
  info: 'bg-blue-50',
  whatsapp: 'bg-whatsapp-50',
  call: 'bg-call-50',
  email: 'bg-email-50',
};

export const TestConversationIcon = ({
  appearance = 'soft',
  className,
  inlineContent = false,
  size = 'xs',
  variant = 'warning',
}: TestConversationIconProps) => {
  const iconSizeClassName = inlineContent
    ? inlineIconSizeClasses[size]
    : iconSizeClasses[size];
  const accentBackgroundClassName =
    appearance === 'soft'
      ? accentBackgroundClassMap[variant]
      : accentBackgroundClassMap.warning;

  return (
    <span
      className={classes(
        'relative inline-flex shrink-0 items-center justify-center',
        iconSizeClassName,
        className
      )}
      aria-hidden="true"
    >
      <PiChatCircle className="h-full w-full" />

      <span
        className={classes(
          'absolute rounded-full',
          accentBackgroundClassName,
          accentContainerClassesBySize[size]
        )}
      >
        <PiTestTubeFill className={accentClassesBySize[size]} />
      </span>
    </span>
  );
};
